/**
 * usePublishedPlans — the C Mode closing-loop data (SPEC-01 §6.4).
 *
 * For the cycles this viewer's sessions belong to, find the current cycle
 * report (Brief G read model) when it has been *published* — that is the
 * moment the loop closes for participants ("the priorities you helped rank
 * are now the plan"). Read-only over existing rows; no new engine, no schema.
 */

import { useEffect, useMemo, useState } from 'react';
import { useCycleReportStore, type CycleReport } from '../../../stores/prioritization/cycleReportStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';

export interface PublishedPlan {
  cycleId: string;
  report: CycleReport;
}

export function usePublishedPlans(mySessions: PrioritizationSession[]): PublishedPlan[] {
  const fetchCurrentCycleReport = useCycleReportStore(s => s.fetchCurrentCycleReport);
  const [plans, setPlans] = useState<PublishedPlan[]>([]);

  // Only cycles where the viewer actually contributed a completed session.
  const cycleIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const s of mySessions) {
      if (s.cycleId && (s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED')) {
        ids.add(s.cycleId);
      }
    }
    return [...ids].sort().join(',');
  }, [mySessions]);

  useEffect(() => {
    if (!cycleIdsKey) {
      setPlans([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const results: PublishedPlan[] = [];
      for (const cycleId of cycleIdsKey.split(',')) {
        const report = await fetchCurrentCycleReport(cycleId);
        if (report && report.legitimacyState === 'published') {
          results.push({ cycleId, report });
        }
      }
      if (!cancelled) setPlans(results);
    })();
    return () => { cancelled = true; };
  }, [cycleIdsKey, fetchCurrentCycleReport]);

  return plans;
}
